import { ButtonCus } from '@/components/common/Button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { RadioGroup, RadioGroupBtnItem } from '@/components/common/RadioGroup';
import { TEST_EK_ANSWER } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ANSWER_TYPE } from '@/types/enums';
import { yupResolver } from '@hookform/resolvers/yup';
import { memo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface TestFormProps {
    onSubmit?: (data) => void
}

const NUM_QUESTIONS = 11;
const questionKeys = Array.from(
    { length: NUM_QUESTIONS },
    (_, i) => `ques${i + 1}`
);

const TestForm = ({ onSubmit }: TestFormProps) => {
    // hook
    const { t } = useTranslation();
    const formRef = useRef<HTMLHeadingElement | null>(null);

    // store
    const {
        authStore: { answersTest, showAnswer, isCompleteTestEK, setObservable }
    } = useStore();

    // form
    const validateSchema = yup.object(
        Object.fromEntries(
            questionKeys.map(key => [
                key,
                yup.boolean()
                    // .required('test.required_answer')
                    .test(
                        'test-required-answer',
                        t('test.required_answer'),
                        (value) => {
                            if (value === true || value === false) return true;
                            return false;
                        }
                    )
                    .nullable()
            ])
        )
    );

    type TestFormData = yup.InferType<typeof validateSchema>;
    const defaultValues: Record<(typeof questionKeys)[number], null> =
        Object.fromEntries(questionKeys.map(key => [key, null]));

    const form = useForm<TestFormData>({
        mode: 'onChange',
        resolver: yupResolver(validateSchema),
        defaultValues: Object.fromEntries(
            questionKeys.map(key => [
                key,
                typeof answersTest?.[key] === 'boolean' ? answersTest[key] : null
            ])
        ) as TestFormData
    });

    // function
    const onFormSubmit = async (data: TestFormData) => {
        let dataSubmit = { ...data };
        if (dataSubmit) {
            setObservable('answersTest', dataSubmit);
            setObservable('showAnswer', true);
        }
        if (onSubmit) {
            onSubmit(dataSubmit);
        }
    };

    const onRedoTest = () => {
        setObservable('answersTest', undefined);
        setObservable('showAnswer', false);

        form.reset({ ...defaultValues });

        if (formRef.current) {
            const top = formRef.current.offsetTop - 15;
            window.scrollTo({
                top,
                behavior: 'smooth'
            });
        }
    };

    return (
        <Form {...form}>
            <h2 className='title-2 mb-2' ref={formRef}>
                {t('test.title_content')}
            </h2>
            <div className=''>{t('test.description')}</div>

            <form className='flex flex-col gap-6 justify-center mt-6'>
                <div className='grid grid-cols-1 gap-4'>
                    {questionKeys?.map((key, index) => (
                        <FormField
                            key={key}
                            control={form.control}
                            name={key}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>
                                        {t('test.question_number', { number: index + 1 })}
                                    </FormLabel>
                                    <div className='mb-3'>
                                        {t(`test.questions.quest_${index + 1}`)}
                                    </div>
                                    <FormControl>
                                        <RadioGroup
                                            key={field.value?.toString()}
                                            onValueChange={(value) => {
                                                field.onChange(value === 'true' || value === 'false' ? value : undefined);
                                            }}
                                            value={
                                                field.value?.toString() === 'true' || field.value?.toString() === 'false' ?
                                                    field.value?.toString() :
                                                    undefined
                                            }
                                            className='w-full flex gap-1'
                                            disabled={showAnswer}
                                        >
                                            <div className='flex items-center justify-center w-[50%]'>
                                                <RadioGroupBtnItem
                                                    value={ANSWER_TYPE.YES.toString()}
                                                    id={`yes${index + 1}`}
                                                    label={t('test.answer.true_symbol')}
                                                    className='w-full rounded-r-none'
                                                />
                                            </div>
                                            <div className='flex items-center justify-center w-[50%]'>
                                                <RadioGroupBtnItem
                                                    value={ANSWER_TYPE.NO.toString()}
                                                    id={`no${index + 1}`}
                                                    label={t('test.answer.false_symbol')}
                                                    className='w-full rounded-l-none'
                                                />
                                            </div>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage />
                                    {showAnswer && (
                                        <Answer isCorrect={form.watch(key)?.toString() === TEST_EK_ANSWER[`QUESTION_${index + 1}`].toString()} />
                                    )}
                                </FormItem>
                            )}
                        />
                    ))}
                </div>
                <div className='flex justify-center mt-4'>
                    {isCompleteTestEK ?
                        (
                            <ButtonCus
                                type='button'
                                variant='outline'
                                size='md'
                                disabled
                            >
                                {t('button.complete')}
                            </ButtonCus>
                        ) :
                        (
                            showAnswer ?
                                (
                                    <ButtonCus
                                        type='button'
                                        variant='outline'
                                        size='md'
                                        onClick={() => onRedoTest()}
                                    >
                                        {t('button.try_again')}
                                    </ButtonCus>
                                ) :
                                (
                                    <ButtonCus
                                        type='button'
                                        variant='outline'
                                        size='md'
                                        onClick={form.handleSubmit(onFormSubmit)}
                                    >
                                        {t('button.complete')}
                                    </ButtonCus>
                                )
                        )}
                </div>
            </form>
        </Form>
    );
};

export default memo(TestForm);

interface AnswerProps {
    isCorrect: boolean
}

const Answer = ({ isCorrect }: AnswerProps) => {
    const { t } = useTranslation();

    return (
        <div className={`mt-2 ${isCorrect ? 'text-text-18' : 'text-text-19'}`}>
            {
                isCorrect ?
                    `${t('test.answer.correct')}` :
                    `${t('test.answer.incorrect')}`
            }
        </div>
    );
};
